<template>
  <div class="fcom_calendar_header">
    <!-- Navigation + title -->
    <div class="fcom_calendar_nav">
      <button class="fcom_calendar_nav-btn" :aria-label="__('previous')" @click="$emit('navigate-previous')">
        <i class="fcom_icon" v-html="icons.arrowLeft" />
      </button>

      <div class="fcom_calendar_title_wrapper">
        <button class="fcom_calendar_title" :aria-label="__('select_month')" @click="toggleDropdown">
          {{ headerLabel }}
          <i class="fcom_icon fcom_calendar_dropdown_icon" v-html="icons.chevronDown" />
        </button>

        <Transition name="fcom_dropdown">
          <div v-if="dropdownOpen" ref="monthSelectorRef" class="fcom_month_selector">
            <div class="fcom_month_selector-grid">
              <button
                v-for="m in monthSelectorItems"
                :key="m.value"
                :class="['fcom_month_selector-option', { 'is-current': m.value === currentYearMonth }]"
                @click="selectMonth(m.date)"
              >
                {{ m.label }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <button class="fcom_calendar_nav-btn" :aria-label="__('next')" @click="$emit('navigate-next')">
        <i class="fcom_icon" v-html="icons.arrowRight" />
      </button>

      <button
        :class="['fcom_calendar_today_btn', { 'is-hidden': isCurrentPeriod }]"
        @click="$emit('go-today')"
      >
        {{ __('today') }}
      </button>
    </div>

    <!-- View toggle -->
    <div class="fcom_calendar_view_toggle" role="group" :aria-label="__('change_calendar_view')">
      <button
        :class="['fcom_view_toggle_btn', { active: viewType === 'month' }]"
        :aria-pressed="viewType === 'month'"
        @click="changeView('month')"
      >
        {{ __('month') }}
      </button>
      <button
        :class="['fcom_view_toggle_btn', { active: viewType === 'week' }]"
        :aria-pressed="viewType === 'week'"
        @click="changeView('week')"
      >
        {{ __('week') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { __ } from '@/utils/i18n.js'
import icons from '@/utils/icons.js'
import { getWeekStart, formatMonthHeader, buildMonthSelector } from '@/utils/calendar.js'

const props = defineProps({
  currentDate:  { type: Object, required: true },
  viewType:     { type: String, default: 'month' },
  weekStartDay: { type: String, default: 'monday' },
})

const emit = defineEmits(['navigate-previous', 'navigate-next', 'go-today', 'view-change', 'month-select'])

const dropdownOpen     = ref(false)
const monthSelectorRef = ref(null)

const headerLabel = computed(() => {
  if (props.viewType === 'week') {
    const start = getWeekStart(props.currentDate, props.weekStartDay)
    const end   = start.plus({ days: 6 })
    if (start.month === end.month) {
      return start.toFormat('MMMM d') + ' - ' + end.toFormat('d, yyyy')
    } else if (start.year === end.year) {
      return start.toFormat('MMM d') + ' - ' + end.toFormat('MMM d, yyyy')
    }
    return start.toFormat('MMM d, yyyy') + ' - ' + end.toFormat('MMM d, yyyy')
  }
  return formatMonthHeader(props.currentDate)
})

const currentYearMonth    = computed(() => props.currentDate.toFormat('yyyy-MM'))
const monthSelectorItems  = computed(() => buildMonthSelector(props.currentDate, 12))

const isCurrentPeriod = computed(() => {
  const now = DateTime.now()
  if (props.viewType === 'month') return props.currentDate.hasSame(now, 'month')
  const weekStart    = getWeekStart(props.currentDate, props.weekStartDay)
  const nowWeekStart = getWeekStart(now, props.weekStartDay)
  return weekStart.hasSame(nowWeekStart, 'day')
})

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function selectMonth(date) {
  dropdownOpen.value = false
  emit('month-select', date)
}

function changeView(view) {
  if (props.viewType !== view) emit('view-change', view)
}

function onDocClick(e) {
  if (monthSelectorRef.value && !monthSelectorRef.value.contains(e.target)) {
    if (!e.target.closest('.fcom_calendar_title')) {
      dropdownOpen.value = false
    }
  }
}

onMounted(() => document.addEventListener('click', onDocClick))
onUnmounted(() => document.removeEventListener('click', onDocClick))
</script>
